import React, { useMemo } from "react";
import moment from "moment";
import { Row, Col } from "react-bootstrap";
import { useParams } from "react-router";

import { TextWithLoader, Mid } from "components";
import { getDurationFromSeconds } from "helpers";
import { getAppStatuses } from "mappers";
import { useCheckIfDocumentExistsMutation } from "queries";
import { MerchantInfoViewType, MerchantTerminalParams } from "types";

type MerchantInfoViewProps = {
	loading?: boolean;
	merchant?: MerchantInfoViewType;
	page?: "Installation" | "TID & Equipment";
};
const DATE_TIME_FORMAT = "DD MMMM YYYY h:mm A";

const fieldDictionary = {
	unassigned: "Unassigned",
	"in-progress": "In Progress",
	pended: "Pended",
	complete: "Complete",
	completed: "Completed",
	"qa-unassigned": "QA Unassigned",
	"qa-in-progress": "QA In Progress",
	"qa-complete": "QA Complete",
};

// Use on Tid Request, Deployment, Equipment details pages
const MerchantInfoView = ({
	loading = false,
	merchant,
	page = "Installation",
}: MerchantInfoViewProps) => {
	const { tab, id } = useParams<MerchantTerminalParams>();

	const getUtcLocal = date => moment.utc(date).local();

	const renderStatusName = tab => {
		let status = tab;
		if (tab in fieldDictionary) {
			status = fieldDictionary[tab];
		}
		return status;
	};

	const activeDate = useMemo(
		() =>
			merchant?.activeDate &&
			getUtcLocal(merchant?.activeDate).format("DD MMMM YYYY"),
		[merchant?.activeDate],
	);

	const ageInRequest = useMemo(() => {
		const endDate = getUtcLocal(
			merchant?.endDate ? merchant?.endDate : merchant?.startDate,
		);

		const dateTime = moment.utc(endDate).local().format(DATE_TIME_FORMAT);
		const ageInInstallationInSeconds =
			merchant?.ageInInstallationInSeconds || 0;

		return (
			<>
				{dateTime}
				<br />
				{getDurationFromSeconds(ageInInstallationInSeconds)}
			</>
		);
	}, [
		merchant?.ageInInstallationInSeconds,
		merchant?.endDate,
		merchant?.startDate,
	]);

	const ageOfPend = useMemo(() => {
		const startDate = getUtcLocal(merchant?.startPendDate);
		const dateTime = startDate.format(DATE_TIME_FORMAT);
		const duration = startDate.fromNow(true);

		return merchant?.startPendDate ? (
			<>
				{dateTime}
				<br />
				{duration}
			</>
		) : null;
	}, [merchant?.startPendDate]);

	const processorSponsor = useMemo(() => {
		return `${merchant?.processor ? merchant?.processor : ""} / ${
			merchant?.sponsor ? merchant?.sponsor : ""
		}`;
	}, [merchant?.processor, merchant?.sponsor]);

	const { mutate: checkFullAppDocument } = useCheckIfDocumentExistsMutation();

	const showViewFullAppDocument = id => {
		checkFullAppDocument({
			id,
			errorLink: `/applications/${id}/view-document/error`,
		});
	};

	return (
		<Row>
			<Col md="5">
				<TextWithLoader
					label="Agent Office"
					value={merchant?.agentOffice}
					loading={loading}
					linkTo={
						merchant?.agentOfficeId &&
						`/system-setup/agent-office-setup/edit-agent-office/${merchant?.agentOfficeId}/general-info`
					}
				/>
				<TextWithLoader
					label="Processor/Sponsor"
					value={processorSponsor}
					loading={loading}
				/>
				<TextWithLoader
					label={`Age in ${page} Request`}
					value={ageInRequest}
					loading={loading}
				/>
				<TextWithLoader
					label="Merchant DBA"
					value={merchant?.merchantName}
					loading={loading}
					handleClick={e => {
						e.stopPropagation();
						showViewFullAppDocument(id);
					}}
					valueClass="overflow-break-word"
				/>
				<TextWithLoader
					label="MID"
					value={<Mid merchant={merchant} />}
					loading={loading}
					linkTo={`/merchant/list/info/${merchant?.merchantId}/general`}
				/>
				<TextWithLoader
					label="Active Date"
					value={activeDate}
					loading={loading}
				/>
				<TextWithLoader
					label="Status"
					value={renderStatusName(tab)}
					loading={loading}
				/>
			</Col>
			<Col md={{ span: 5, offset: 2 }}>
				<TextWithLoader
					label="Agent Name"
					value={merchant?.agent}
					loading={loading}
					linkTo={
						merchant?.agentId &&
						`/system-setup/agent-setup/edit-agent/${merchant?.agentId}/general-info`
					}
				/>
				<TextWithLoader
					label="Industry Type"
					value={merchant?.industryType}
					loading={loading}
				/>
				<TextWithLoader
					label="Age of Pend"
					value={ageOfPend}
					loading={loading}
				/>
				<TextWithLoader
					label="Application Status"
					value={getAppStatuses(merchant?.applicationStatus)}
					loading={loading}
				/>
				<TextWithLoader
					label="Assignee"
					value={merchant?.assignee}
					loading={loading}
				/>
				<TextWithLoader
					label="Open Pend Count"
					value={merchant?.openPendCount}
					loading={loading}
				/>
			</Col>
		</Row>
	);
};

export default MerchantInfoView;
